import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Button,
  TextField,
  IconButton,
  Popover,
  Checkbox,
  ListItemText,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  List,
  Typography,
  Divider,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Badge,
  Chip,
  Grid
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';

import { environment } from '../../environments/environment';
import { useNavigate } from 'react-router-dom';
import AddButton from 'datascient-ui-library/src/lib/Molecules/AddButton/AddButton';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Chip as MuiChip } from '@mui/material';
import moment from 'moment';
import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DescriptionIcon from '@mui/icons-material/Description';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TimelineIcon from '@mui/icons-material/Timeline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import InfoIcon from '@mui/icons-material/Info';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CategoryIcon from '@mui/icons-material/Category';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SpeedIcon from '@mui/icons-material/Speed';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SavingsIcon from '@mui/icons-material/Savings';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { debounce } from 'lodash';
import CircularProgress from '@mui/material/CircularProgress';
import { ListAlt } from '@mui/icons-material';
import AnalyticsVisualizations from './AnalyticsVisualizations';
import { useTheme, useMediaQuery } from '@mui/material';
import { dsVar, ink } from '../theme/tokens';

const StatusCell = ({ status }) => {
  const statusLower = status?.toLowerCase();
  const isSuccess = statusLower === 'success';
  const isPending = statusLower === 'pending user input';

  return (
    <TableCell align="center">
      <Chip
        label={isSuccess ? 'Success' : isPending ? 'Pending User Input' : 'Failed'}
        sx={{
          borderRadius: '6px',
          backgroundColor: isSuccess ? 'rgba(46, 125, 50, 0.2)' : isPending ? 'rgba(230, 81, 0, 0.2)' : 'rgba(198, 40, 40, 0.2)',
          color: isSuccess ? ink('#4ade80') : isPending ? ink('#fb923c') : ink('#f87171'),
          fontWeight: 500,
          fontSize: '13px',
          padding: '4px 12px',
          border: `1px solid ${isSuccess ? 'rgba(74, 222, 128, 0.2)' : isPending ? 'rgba(251, 146, 60, 0.2)' : 'rgba(248, 113, 113, 0.2)'}`
        }}
      />
    </TableCell>
  );
};

const AdminPanel = () => {
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedReferenceId, setSelectedReferenceId] = useState(null);
  const [selectedName, setSelectedName] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [flowTemplateOptions, setFlowTemplateOptions] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeDetails, setNodeDetails] = useState(null);
  const [treeData, setTreeData] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set(['root']));
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsInterval, setAnalyticsInterval] = useState('day');
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD')
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Add demo data for dashboard metrics


  // Add demo data for charts



  const MetricCard = ({ title, value, change, trend, period, icon, color }) => (
    <Card sx={{
      height: '100%',
      borderRadius: '12px',
      backgroundColor: dsVar('surface'),
      border: `1px solid ${dsVar('line')}`,
      boxShadow: 'none',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        border: '1px solid rgba(255, 222, 89, 0.3)',
        transform: 'translateY(-2px)'
      }
    }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{
            backgroundColor: 'rgba(255, 222, 89, 0.1)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: trend === 'up' ? 'rgba(46, 125, 50, 0.15)' : 'rgba(198, 40, 40, 0.15)',
            borderRadius: '12px',
            px: 1,
            py: 0.5
          }}>
            {trend === 'up' ? (
              <TrendingUpIcon sx={{ color: ink('#4ade80'), fontSize: 14 }} />
            ) : (
              <TrendingDownIcon sx={{ color: ink('#f87171'), fontSize: 14 }} />
            )}
            <Typography sx={{
              color: trend === 'up' ? ink('#4ade80') : ink('#f87171'),
              fontSize: '12px',
              fontWeight: 500
            }}>
              {change}
            </Typography>
          </Box>
        </Box>
        <Typography sx={{
          color: dsVar('accent-fg'),
          fontSize: '20px',
          fontWeight: 600,
          mb: 0.5
        }}>
          {value}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{
            color: dsVar('fg-2'),
            fontSize: '13px'
          }}>
            {title}
          </Typography>
          <Typography sx={{
            color: dsVar('fg-muted'),
            fontSize: '11px'
          }}>
            {period}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  const ChartCard = ({ title, children }) => (
    <Card sx={{
      borderRadius: '12px',
      backgroundColor: dsVar('surface'),
      border: `1px solid ${dsVar('line')}`,
      boxShadow: 'none',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        border: '1px solid rgba(255, 222, 89, 0.3)',
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Typography sx={{
          color: dsVar('fg-body'),
          fontSize: '16px',
          fontWeight: 600,
          mb: 2
        }}>
          {title}
        </Typography>
        <Box sx={{
          width: '100%',
          height: '300px'
        }}>
          {children}
        </Box>
      </CardContent>
    </Card>
  );

  const getActivityFlowLog = async () => {
    try {
      setIsLoading(true);
      const data = {
        data: {
          org_id: localStorage.getItem('org_id'),

        }
      };
      const res = await axios.post(
        `${environment.controlplaneUrl}/analytics/activity-flow-logs`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      // Handle new response structure with hits array
      const responseData = res?.data?.hits || [];
      setTableData(responseData);
      setFilteredData(responseData);

      // Extract flow_template_name options for filter
      const uniqueFlowTemplates = Array.from(new Set(responseData.map(item => item.productName)));
      setFlowTemplateOptions(uniqueFlowTemplates);
    } catch (error) {
      console.log('error', error);
      if (
        error?.response?.data?.error === 'jwt malformed' ||
        error?.response?.data?.error === 'jwt expired'
      ) {
        console.log(error?.response?.data?.error);
        navigate('/login');
      }
      // Set empty arrays on error to prevent further issues
      setTableData([]);
      setFilteredData([]);
      setFlowTemplateOptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const getActivityFlowLogDetail = (referenceId) => {
    try {
      // Find the flow log data from the existing tableData using referenceId
      const flowLogData = tableData.find(item => item.referenceId === referenceId);

      if (!flowLogData) {
        console.error('Flow log data not found for referenceId:', referenceId);
        return;
      }

      const { configuration, industry, language, geography, status, date } = flowLogData;
      const nodes = configuration?.nodes || [];
      const edges = configuration?.edges || [];

      // Debug: Log the configuration to see what's available
      console.log('Configuration:', configuration);
      console.log('Configuration keys:', Object.keys(configuration || {}));

      // Create a map of nodes for easy lookup
      const nodeMap = new Map(nodes.map(node => [node.data.id, node]));

      // Build the flow structure
      const buildFlowStructure = () => {
        // Find root nodes (nodes with no incoming edges)
        const rootNodes = nodes.filter(node =>
          !edges.some(edge => edge.data.target === node.data.id)
        );

        // Build the tree structure recursively
        const buildTree = (node, level = 0) => {
          // Get all outgoing edges for this node
          const outgoingEdges = edges.filter(edge => edge.data.source === node.data.id);

          // Debug: Log the node data to see what's available
          console.log('Node data in buildTree:', node.data);
          console.log('Node data keys in buildTree:', Object.keys(node.data || {}));

          // Create the node structure
          const nodeStructure = {
            id: node.data.id,
            name: node.data.activityTypeName || node.data.activityTemplateCode,
            templateName: node.data.activityTemplateName || node.data.activityTemplateCode,
            type: 'file',
            data: node.data,
            level: level,
            children: []
          };

          if (outgoingEdges.length === 1) {
            // Single edge - sequential flow
            const targetNode = nodeMap.get(outgoingEdges[0].data.target);
            if (targetNode) {
              // For sequential flows, keep the same level
              nodeStructure.children = [buildTree(targetNode, level)];
            }
          } else if (outgoingEdges.length > 1) {
            // Multiple edges - branching flow
            nodeStructure.children = outgoingEdges.map(edge => {
              const targetNode = nodeMap.get(edge.data.target);
              if (targetNode) {
                // For branching flows, increase the level
                return buildTree(targetNode, level + 1);
              }
              return null;
            }).filter(Boolean);
          }

          return nodeStructure;
        };

        // Create the root structure
        return {
          id: 'root',
          name: 'Agent Run',
          type: 'folder',
          children: rootNodes.map(node => buildTree(node, 0))
        };
      };

      const flowStructure = buildFlowStructure();
      setTreeData(flowStructure);

      // Expand all nodes by default
      const allNodeIds = new Set(['root']);
      const collectNodeIds = (node) => {
        if (node.id) allNodeIds.add(node.id);
        if (node.children) {
          node.children.forEach(child => collectNodeIds(child));
        }
      };
      collectNodeIds(flowStructure);
      setExpandedNodes(allNodeIds);

      // Remove default node selection
      setSelectedNode(null);

      // Debug: Log the flow log data to see what's available
      console.log('Flow log data:', flowLogData);
      console.log('Flow log data keys:', Object.keys(flowLogData || {}));

      setNodeDetails({
        industry,
        language,
        geography,
        status,
        date,
        // Check if parameters exist at flow level - try multiple possible locations
        parameters: flowLogData?.parameters ||
          flowLogData?.config ||
          flowLogData?.settings ||
          flowLogData?.data?.parameters ||
          flowLogData?.data?.config ||
          flowLogData?.data?.settings ||
          {}
      });
    } catch (error) {
      console.error('Error processing flow log detail:', error);
    }
  };

  const handleNodeClick = (node) => {
    try {
      setSelectedNode(node.id);

      // Debug: Log the node data to see what's available
      console.log('Node data:', node.data);
      console.log('Node data keys:', Object.keys(node.data || {}));

      // Use the node data directly from the tree structure
      setNodeDetails({
        activityTypeName: node.name,
        activityTemplateName: node.templateName,
        parameters: node.data?.parameters ||
          node.data?.config ||
          node.data?.settings ||
          node.data?.data?.parameters ||
          node.data?.data?.config ||
          node.data?.data?.settings ||
          {},
        industry: nodeDetails?.industry,
        language: nodeDetails?.language,
        geography: nodeDetails?.geography,
        status: nodeDetails?.status,
        date: nodeDetails?.date
      });
    } catch (error) {
      console.error('Error processing node click:', error);
    }
  };

  useEffect(() => {
    getActivityFlowLog();
  }, []);

  useEffect(() => {
    if (selectedReferenceId) {
      getActivityFlowLogDetail(selectedReferenceId);
    }
  }, [selectedReferenceId, tableData]);

  // Create a debounced search function
  const debouncedSearch = React.useCallback(
    debounce((searchValue, filters) => {
      if (!searchValue && filters.length === 0) {
        setFilteredData(tableData);
        return;
      }

      const filtered = tableData.filter(item => {
        const matchesSearch = item.instanceName.toLowerCase().includes(searchValue.toLowerCase());
        const matchesFilter = filters.length === 0 || filters.includes(item.productName);
        return matchesSearch && matchesFilter;
      });

      setFilteredData(filtered);
    }, 300),
    [tableData]
  );

  const handleSearchChange = (event) => {
    const searchValue = event.target.value;
    setSearchTerm(searchValue);
    debouncedSearch(searchValue, selectedFilters);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (name) => {
    setSelectedFilters(prevSelected => {
      const newSelected = prevSelected.includes(name)
        ? prevSelected.filter(filter => filter !== name)
        : [...prevSelected, name];

      debouncedSearch(searchTerm, newSelected);
      return newSelected;
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (data) => {
    setSelectedReferenceId(data.referenceId);
    setSelectedName(data.instanceName);
  };

  const handleBackClick = () => {
    setSelectedReferenceId(null);
  };

  const open = Boolean(filterAnchorEl);
  const id = open ? 'filter-popover' : undefined;

  const formatDateString = (dateString) => {
    if (!dateString) {
      return 'N/A';
    }

    try {
      // Handle the new date format: "2025-06-11, 09:20:30:621074"
      // Replace the extra colon with a dot to match the expected format
      const normalizedDateString = dateString.replace(/:(\d{6})$/, '.$1');

      // Parse the date string with moment
      const formattedDate = moment(normalizedDateString, 'YYYY-MM-DD, HH:mm:ss.SSSSSS').format('MMM DD, YYYY HH:mm:ss');

      // Check if the date is valid
      if (formattedDate === 'Invalid date') {
        return 'Invalid date';
      }

      return formattedDate;
    } catch (error) {
      console.warn('Error formatting date:', dateString, error);
      return 'Invalid date';
    }
  };

  const renderFlowStructure = (node) => {
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedNode === node.id;

    if (node.type === 'folder') {
      return (
        <Box key={node.id} sx={{ pl: node.id !== 'root' ? 1 : 0 }}>
          <Box
            onClick={() => toggleNode(node.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              borderRadius: '12px',
              backgroundColor: isExpanded ? 'rgba(255, 222, 89, 0.1)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 222, 89, 0.05)',
              },
              transition: 'all 0.2s ease-in-out',
              boxShadow: isExpanded ? '0 0 15px rgba(255, 222, 89, 0.05)' : 'none',
              mb: 2,
              p: 1
            }}
          >
            <Box sx={{
              backgroundColor: 'rgba(255, 222, 89, 0.15)',
              borderRadius: '12px',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2
            }}>
              <AccountTreeIcon sx={{ color: dsVar('accent-fg'), fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="body1" sx={{
                fontWeight: 600,
                color: dsVar('fg-body'),
                fontSize: '16px'
              }}>
                {node.name}
              </Typography>
              <Typography variant="caption" sx={{
                color: dsVar('fg-2'),
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.5
              }}>
                <PlayCircleOutlineIcon sx={{ fontSize: 12, color: dsVar('accent-fg') }} />
                Flow Execution
              </Typography>
            </Box>
          </Box>
          {isExpanded && node.children && (
            <Box sx={{
              pl: 3,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                width: '3px',
                height: '100%',
                background: `linear-gradient(180deg, ${dsVar('accent')} 0%, rgba(255, 222, 89, 0.2) 100%)`,
                borderRadius: '4px',
                boxShadow: '0 0 8px rgba(255, 222, 89, 0.2)'
              }
            }}>
              {node.children.map((child, index) => (
                <Box key={child.id} sx={{ position: 'relative' }}>
                  {index < node.children.length - 1 && (
                    <Box sx={{
                      position: 'absolute',
                      left: '-3px',
                      top: '50%',
                      width: '3px',
                      height: '100%',
                      background: `linear-gradient(180deg, ${dsVar('accent')} 0%, rgba(255, 222, 89, 0.1) 100%)`,
                      borderRadius: '4px',
                      boxShadow: '0 0 8px rgba(255, 222, 89, 0.2)'
                    }} />
                  )}
                  {renderFlowStructure(child)}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    return (
      <Box key={node.id} sx={{ pl: node.level * 2 }}>
        <Box
          onClick={() => handleNodeClick(node)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            cursor: 'pointer',
            borderRadius: '12px',
            backgroundColor: isSelected ? 'rgba(255, 222, 89, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(255, 222, 89, 0.05)',
            },
            transition: 'all 0.2s ease-in-out',
            position: 'relative',
            boxShadow: isSelected ? '0 2px 8px rgba(255, 222, 89, 0.05)' : 'none',
            mb: 2,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '-12px',
              top: '50%',
              width: '12px',
              height: '3px',
              background: `linear-gradient(90deg, rgba(255, 222, 89, 0.2) 0%, ${dsVar('accent')} 100%)`,
              transform: 'translateY(-50%)',
              borderRadius: '4px',
              boxShadow: '0 0 8px rgba(255, 222, 89, 0.2)'
            }
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: 1
          }}>
            <Box sx={{
              backgroundColor: isSelected ? 'rgba(255, 222, 89, 0.1)' : dsVar('fg-muted', 0.1),
              borderRadius: '12px',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {(() => {
                const getRandomIcon = (activityType) => {
                  const icons = [
                    <CategoryIcon sx={{ color: isSelected ? dsVar('accent-fg') : dsVar('fg-2'), fontSize: 20 }} />,
                    <DescriptionIcon sx={{ color: isSelected ? dsVar('accent-fg') : dsVar('fg-2'), fontSize: 20 }} />,
                    <TrendingUpIcon sx={{ color: isSelected ? dsVar('accent-fg') : dsVar('fg-2'), fontSize: 20 }} />,
                    <TaskAltIcon sx={{ color: isSelected ? dsVar('accent-fg') : dsVar('fg-2'), fontSize: 20 }} />,
                    <TimelineIcon sx={{ color: isSelected ? dsVar('accent-fg') : dsVar('fg-2'), fontSize: 20 }} />,
                    <AccountTreeIcon sx={{ color: isSelected ? dsVar('accent-fg') : dsVar('fg-2'), fontSize: 20 }} />,
                    <InfoIcon sx={{ color: isSelected ? dsVar('accent-fg') : dsVar('fg-2'), fontSize: 20 }} />,
                    <AccessTimeIcon sx={{ color: isSelected ? dsVar('accent-fg') : dsVar('fg-2'), fontSize: 20 }} />
                  ];

                  const hash = activityType?.split('').reduce((acc, char) => {
                    return char.charCodeAt(0) + ((acc << 5) - acc);
                  }, 0);

                  const index = Math.abs(hash) % icons.length;
                  return icons[index];
                };

                return getRandomIcon(node.data?.activityType);
              })()}
            </Box>
            <Box>
              <Typography variant="body2" sx={{
                fontWeight: 600,
                color: isSelected ? dsVar('accent-fg') : dsVar('fg-body'),
                fontSize: '14px'
              }}>
                {node.name}
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 0.5
              }}>
                {node.data?.execution_time && (
                  <Typography variant="caption" sx={{
                    color: dsVar('fg-muted'),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    backgroundColor: dsVar('fg-muted', 0.1),
                    borderRadius: '8px',
                    px: 1,
                    py: 0.5
                  }}>
                    <SpeedIcon sx={{ fontSize: 12, color: dsVar('accent-fg') }} />
                    {node.data.execution_time.toFixed(2)}s
                  </Typography>
                )}

              </Box>
            </Box>
          </Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            {node.data?.status && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backgroundColor: (() => {
                  const statusLower = node.data.status.toLowerCase();
                  if (statusLower === 'success') return 'rgba(46, 125, 50, 0.2)';
                  if (statusLower === 'pending user input') return 'rgba(230, 81, 0, 0.2)';
                  return 'rgba(198, 40, 40, 0.2)';
                })(),
                borderRadius: '8px',
                p: 1
              }}>
                {(() => {
                  const statusLower = node.data.status.toLowerCase();
                  if (statusLower === 'success') return <CheckCircleIcon sx={{ color: ink('#4ade80'), fontSize: 16 }} />;
                  if (statusLower === 'pending user input') return <AccessTimeIcon sx={{ color: ink('#fb923c'), fontSize: 16 }} />;
                  return <ErrorIcon sx={{ color: ink('#f87171'), fontSize: 16 }} />;
                })()}
                <Typography variant="caption" sx={{
                  color: (() => {
                    const statusLower = node.data.status.toLowerCase();
                    if (statusLower === 'success') return '#4ade80';
                    if (statusLower === 'pending user input') return '#fb923c';
                    return '#f87171';
                  })(),
                  fontWeight: 500
                }}>
                  {node.data.status}
                </Typography>
              </Box>
            )}
            {node.children && node.children.length > 0 && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: dsVar('fg-muted', 0.1),
                borderRadius: '8px',
                p: 1
              }}>
                <ArrowForwardIcon sx={{
                  color: dsVar('fg-muted'),
                  fontSize: 16
                }} />
                <Typography variant="caption" sx={{
                  color: dsVar('fg-muted'),
                  fontWeight: 500
                }}>

                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        {node.children && node.children.length > 0 && (
          <Box sx={{
            pl: 3,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              width: '3px',
              height: '100%',
              background: `linear-gradient(180deg, ${dsVar('accent')} 0%, rgba(255, 222, 89, 0.1) 100%)`,
              borderRadius: '4px',
              boxShadow: '0 0 8px rgba(255, 222, 89, 0.2)'
            }
          }}>
            {node.children.map((child, index) => (
              <Box key={child.id} sx={{ position: 'relative' }}>
                {index < node.children.length - 1 && (
                  <Box sx={{
                    position: 'absolute',
                    left: '-3px',
                    top: '50%',
                    width: '3px',
                    height: '100%',
                    background: `linear-gradient(180deg, ${dsVar('accent')} 0%, rgba(255, 222, 89, 0.1) 100%)`,
                    borderRadius: '4px',
                    boxShadow: '0 0 8px rgba(255, 222, 89, 0.2)'
                  }} />
                )}
                {renderFlowStructure(child)}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // Add this new function to group agents by name
  const groupAgentsByName = (data) => {
    // Ensure data is an array
    if (!Array.isArray(data)) {
      console.warn('groupAgentsByName received non-array data:', data);
      return {};
    }

    const groupedData = data.reduce((acc, item) => {
      const key = item.instanceName;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});
    return groupedData;
  };

  // Add this new function to get status summary
  const getStatusSummary = (runs) => {
    // Ensure runs is an array
    if (!Array.isArray(runs)) {
      console.warn('getStatusSummary received non-array runs:', runs);
      return {};
    }

    const summary = runs.reduce((acc, run) => {
      acc[run.status] = (acc[run.status] || 0) + 1;
      return acc;
    }, {});
    return summary;
  };

  // Add this new function to get latest run
  const getLatestRun = (runs) => {
    // Ensure runs is an array
    if (!Array.isArray(runs) || runs.length === 0) {
      console.warn('getLatestRun received non-array or empty runs:', runs);
      return null;
    }

    return runs.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
  };

  // Cleanup debounced function on unmount
  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Add analytics fetch function
  const fetchAnalytics = async (interval = 'day', range = dateRange) => {
    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/analytics/time-series/${interval}`,
        {
          startDate: range.startDate,
          endDate: range.endDate,
          filters: {
            orgId: localStorage.getItem('org_id')
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      if (
        error?.response?.data?.error === 'jwt malformed' ||
        error?.response?.data?.error === 'jwt expired'
      ) {
        navigate('/login');
      }
    }
  };

  // Handle interval change
  const handleIntervalChange = (interval) => {
    setAnalyticsInterval(interval);
    fetchAnalytics(interval, dateRange);
  };

  // Handle date range change
  const handleDateRangeChange = (range) => {
    setDateRange(range);
    fetchAnalytics(analyticsInterval, range);
  };

  // Initial data fetch
  useEffect(() => {
    fetchAnalytics(analyticsInterval, dateRange);
  }, []); // Only run on mount

  return (
    <Box sx={{
      backgroundColor: dsVar('page'),
      minHeight: '100vh',
      py: 4,
      px: { xs: 2, md: 4 }
    }}>
      <Box sx={{
        maxWidth: '1600px',
        margin: '0 auto'
      }}>
        {selectedReferenceId ? (
          <Box>
            <AddButton
              text={'Back'}
              icon={<KeyboardBackspaceIcon />}
              sx={{ fontSize: '14px', color: dsVar('accent-fg'), fontWeight: '600', marginLeft: '30px' }}
              handleClick={handleBackClick}
            />
            <Box sx={{
              color: dsVar('fg-body'),
              fontSize: '24px',
              fontWeight: '700',
              marginLeft: '10%',
              textAlign: 'left',
              marginBottom: '10px'
            }}>
              {selectedName}
            </Box>

            <Box sx={{
              display: 'flex',
              gap: '24px',
              height: 'calc(100vh - 200px)',
              mt: 2
            }}>
              {/* Left Panel - Flow Structure (30%) */}
              <Box sx={{
                flex: 3,
                border: `1px solid ${dsVar('line')}`,
                borderRadius: '12px',
                p: 2,
                overflow: 'auto',
                backgroundColor: dsVar('surface'),
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: dsVar('page'),
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: dsVar('line'),
                  borderRadius: '4px',
                  '&:hover': {
                    background: dsVar('accent')
                  }
                },
                '&::-webkit-scrollbar-corner': {
                  background: dsVar('page'),
                }
              }}>
                {treeData && renderFlowStructure(treeData)}
              </Box>

              {/* Middle Panel - Details (50%) */}
              <Box sx={{
                flex: 6,
                border: `1px solid ${dsVar('line')}`,
                borderRadius: '12px',
                p: 2,
                overflow: 'auto',
                backgroundColor: dsVar('surface'),
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: dsVar('page'),
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: dsVar('line'),
                  borderRadius: '4px',
                  '&:hover': {
                    background: dsVar('accent')
                  }
                }
              }}>
                {nodeDetails && (
                  <Box>
                    {!selectedNode ? (
                      <Box sx={{
                        mb: 3,
                        p: 2,
                        backgroundColor: dsVar('surface'),
                        borderRadius: '12px',
                        border: `1px solid ${dsVar('line')}`
                      }}>
                        <Typography variant="subtitle1" sx={{
                          color: dsVar('fg-body'),
                          fontWeight: 600,
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          <TimelineIcon sx={{ color: dsVar('accent-fg') }} />
                          Overview
                        </Typography>
                        <Box sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                          gap: 2
                        }}>
                          <Box sx={{
                            p: 2,
                            backgroundColor: dsVar('page'),
                            borderRadius: '8px',
                            border: `1px solid ${dsVar('line')}`
                          }}>
                            <Typography variant="caption" sx={{ color: dsVar('fg-2') }}>
                              Execution Status
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                label={(() => {
                                  const statusLower = nodeDetails.status?.toLowerCase();
                                  if (statusLower === 'success') return 'Success';
                                  if (statusLower === 'pending user input') return 'Pending User Input';
                                  return 'Failed';
                                })()}
                                sx={{
                                  borderRadius: '16px',
                                  backgroundColor: (() => {
                                    const statusLower = nodeDetails.status?.toLowerCase();
                                    if (statusLower === 'success') return 'rgba(46, 125, 50, 0.2)';
                                    if (statusLower === 'pending user input') return 'rgba(230, 81, 0, 0.2)';
                                    return 'rgba(198, 40, 40, 0.2)';
                                  })(),
                                  color: (() => {
                                    const statusLower = nodeDetails.status?.toLowerCase();
                                    if (statusLower === 'success') return '#4ade80';
                                    if (statusLower === 'pending user input') return '#fb923c';
                                    return '#f87171';
                                  })(),
                                  fontWeight: 500,
                                  fontSize: '13px',
                                  padding: '4px 12px',
                                  border: `1px solid ${dsVar('fg', 0.05)}`
                                }}
                              />
                            </Box>
                          </Box>
                          <Box sx={{
                            p: 2,
                            backgroundColor: dsVar('page'),
                            borderRadius: '8px',
                            border: `1px solid ${dsVar('line')}`
                          }}>
                            <Typography variant="caption" sx={{ color: dsVar('fg-2') }}>
                              Date & Time
                            </Typography>
                            <Typography variant="body2" sx={{
                              color: dsVar('fg-soft'),
                              fontWeight: 500,
                              mt: 1
                            }}>
                              {formatDateString(nodeDetails.date)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 3,
                        p: 2,
                        backgroundColor: dsVar('surface'),
                        borderRadius: '12px',
                        border: `1px solid ${dsVar('line')}`
                      }}>
                        <TaskAltIcon sx={{
                          color: dsVar('accent-fg'),
                          fontSize: 24,
                          backgroundColor: 'rgba(255, 222, 89, 0.1)',
                          borderRadius: '8px',
                          p: 1
                        }} />
                        <Box>
                          <Typography variant="h6" sx={{
                            color: dsVar('fg-body'),
                            fontWeight: 600,
                            mb: 0.5
                          }}>
                            {nodeDetails.activityTypeName}
                          </Typography>
                          <Typography variant="body2" sx={{
                            color: dsVar('fg-2'),
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <PlayArrowIcon sx={{ fontSize: 14, color: dsVar('accent-fg') }} />
                            {nodeDetails.activityTemplateName}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Parameters */}
                    <Box sx={{
                      mb: 3,
                      p: 2,
                      backgroundColor: dsVar('surface'),
                      borderRadius: '12px',
                      border: `1px solid ${dsVar('line')}`
                    }}>
                      <Typography variant="subtitle1" sx={{
                        color: dsVar('fg-body'),
                        fontWeight: 600,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <DescriptionIcon sx={{ color: dsVar('accent-fg') }} />
                        Parameters
                      </Typography>
                      {nodeDetails.parameters && Object.keys(nodeDetails.parameters).length > 0 ? (
                        <Box sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                          gap: 2
                        }}>
                          {Object.entries(nodeDetails.parameters).map(([key, value]) => (
                            <Box key={key} sx={{
                              p: 2,
                              backgroundColor: dsVar('page'),
                              borderRadius: '8px',
                              border: `1px solid ${dsVar('line')}`
                            }}>
                              <Typography variant="caption" sx={{
                                color: dsVar('fg-2'),
                                display: 'block',
                                mb: 0.5
                              }}>
                                {key}
                              </Typography>
                              <Typography variant="body2" sx={{
                                color: dsVar('fg-soft'),
                                fontWeight: 500
                              }}>
                                {typeof value === 'object' ? JSON.stringify(value) : value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{
                          p: 2,
                          backgroundColor: dsVar('page'),
                          borderRadius: '8px',
                          border: `1px solid ${dsVar('line')}`,
                          textAlign: 'center'
                        }}>
                          <Typography variant="body2" sx={{
                            color: dsVar('fg-muted'),
                            fontStyle: 'italic'
                          }}>
                            No parameters available for this activity
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Context */}
                    <Box sx={{
                      p: 2,
                      backgroundColor: dsVar('surface'),
                      borderRadius: '12px',
                      border: `1px solid ${dsVar('line')}`
                    }}>
                      <Typography variant="subtitle1" sx={{
                        color: dsVar('fg-body'),
                        fontWeight: 600,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <AccountTreeIcon sx={{ color: dsVar('accent-fg') }} />
                        Context
                      </Typography>
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: 2
                      }}>
                        <Box sx={{
                          p: 2,
                          backgroundColor: dsVar('page'),
                          borderRadius: '8px',
                          border: `1px solid ${dsVar('line')}`
                        }}>
                          <Typography variant="caption" sx={{ color: dsVar('fg-2') }}>
                            Industry
                          </Typography>
                          <Typography variant="body2" sx={{
                            color: dsVar('fg-soft'),
                            fontWeight: 500,
                            mt: 0.5
                          }}>
                            {nodeDetails.industry}
                          </Typography>
                        </Box>
                        <Box sx={{
                          p: 2,
                          backgroundColor: dsVar('page'),
                          borderRadius: '8px',
                          border: `1px solid ${dsVar('line')}`
                        }}>
                          <Typography variant="caption" sx={{ color: dsVar('fg-2') }}>
                            Language
                          </Typography>
                          <Typography variant="body2" sx={{
                            color: dsVar('fg-soft'),
                            fontWeight: 500,
                            mt: 0.5
                          }}>
                            {nodeDetails.language}
                          </Typography>
                        </Box>
                        <Box sx={{
                          p: 2,
                          backgroundColor: dsVar('page'),
                          borderRadius: '8px',
                          border: `1px solid ${dsVar('line')}`
                        }}>
                          <Typography variant="caption" sx={{ color: dsVar('fg-2') }}>
                            Geography
                          </Typography>
                          <Typography variant="body2" sx={{
                            color: dsVar('fg-soft'),
                            fontWeight: 500,
                            mt: 0.5
                          }}>
                            {nodeDetails.geography}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box>
            {/* Header Section */}


            {/* Analytics Visualizations */}
            {analyticsData && (
              <AnalyticsVisualizations
                data={analyticsData}
                onIntervalChange={handleIntervalChange}
                onDateRangeChange={handleDateRangeChange}
              />
            )}



            {/* Agent List */}
            <Grid container spacing={3}>


              <Grid item xs={12}>
                <Box sx={{
                  height: '55vh',
                  minHeight: '500px',

                  mt: 2,
                  pl: 2,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: dsVar('page'),
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: dsVar('line'),
                    borderRadius: '4px',
                    '&:hover': {
                      background: dsVar('accent'),
                    }
                  }
                }}>
                  <Typography variant="h6" sx={{
                    color: dsVar('fg-body'),
                    fontWeight: 600,
                    mt: 4,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <TimelineIcon sx={{ color: dsVar('accent-fg') }} />
                    Agent Runs
                  </Typography>

                  {isLoading ? (
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      backgroundColor: dsVar('surface'),
                      borderRadius: '12px',
                      border: `1px solid ${dsVar('line')}`,
                      boxShadow: 'none'
                    }}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2
                      }}>
                        <CircularProgress sx={{ color: dsVar('accent-fg') }} />
                        <Typography sx={{
                          color: dsVar('fg-2'),
                          fontSize: '14px'
                        }}>
                          Loading agents...
                        </Typography>
                      </Box>
                    </Box>
                  ) : filteredData.length === 0 ? (
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      backgroundColor: dsVar('surface'),
                      borderRadius: '12px',
                      border: `1px solid ${dsVar('line')}`,
                      boxShadow: 'none'
                    }}>
                      <Typography sx={{
                        color: dsVar('fg-2'),
                        fontSize: '14px'
                      }}>
                        No agents found
                      </Typography>
                    </Box>
                  ) : (
                    (() => {
                      const groupedData = groupAgentsByName(filteredData);
                      const entries = Object.entries(groupedData);

                      if (entries.length === 0) {
                        return (
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            backgroundColor: dsVar('surface'),
                            borderRadius: '12px',
                            border: `1px solid ${dsVar('line')}`,
                            boxShadow: 'none'
                          }}>
                            <Typography sx={{
                              color: dsVar('fg-2'),
                              fontSize: '14px'
                            }}>
                              No agents found
                            </Typography>
                          </Box>
                        );
                      }

                      return entries.map(([agentName, agentRuns]) => {
                        const statusSummary = getStatusSummary(agentRuns);
                        const latestRun = getLatestRun(agentRuns);

                        return (
                          <Accordion
                            key={agentName}
                            sx={{
                              mb: 2,
                              '&:before': {
                                display: 'none',
                              },
                              backgroundColor: dsVar('surface'),
                              border: `1px solid ${dsVar('line')}`,
                              boxShadow: 'none',
                              borderRadius: '12px !important',
                              overflow: 'hidden',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                border: '1px solid rgba(255, 222, 89, 0.3)',
                                transform: 'translateY(-2px)'
                              }
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon sx={{ color: dsVar('fg-2') }} />}
                              sx={{
                                backgroundColor: dsVar('surface'),
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 222, 89, 0.05)',
                                },
                                minHeight: '72px !important',
                              }}
                            >
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                pr: 2
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Box sx={{
                                    backgroundColor: 'rgba(255, 222, 89, 0.1)',
                                    borderRadius: '12px',
                                    p: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <ListAlt sx={{ color: dsVar('accent-fg'), fontSize: 24 }} />
                                  </Box>
                                  <Box>
                                    <Typography sx={{
                                      fontWeight: 600,
                                      color: dsVar('fg-body'),
                                      fontSize: '16px',
                                      mb: 0.5
                                    }}>
                                      {agentName}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Chip
                                        size="small"
                                        label={`${agentRuns.length} runs`}
                                        sx={{
                                          backgroundColor: 'rgba(255, 222, 89, 0.1)',
                                          color: dsVar('accent-fg'),
                                          fontWeight: 500,
                                          fontSize: '12px',
                                          borderRadius: '8px'
                                        }}
                                      />
                                      {statusSummary.success && (
                                        <Chip
                                          size="small"
                                          label={`${statusSummary.success} successful`}
                                          sx={{
                                            backgroundColor: 'rgba(74, 222, 128, 0.1)',
                                            color: ink('#4ade80'),
                                            fontWeight: 500,
                                            fontSize: '12px',
                                            borderRadius: '8px'
                                          }}
                                        />
                                      )}
                                      {statusSummary.failed && (
                                        <Chip
                                          size="small"
                                          label={`${statusSummary.failed} failed`}
                                          sx={{
                                            backgroundColor: 'rgba(248, 113, 113, 0.1)',
                                            color: ink('#f87171'),
                                            fontWeight: 500,
                                            fontSize: '12px',
                                            borderRadius: '8px'
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 3
                                }}>
                                  {latestRun && (
                                    <>
                                      <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: dsVar('fg-muted')
                                      }}>
                                        <AccessTimeIcon sx={{ fontSize: 16, color: dsVar('accent-fg') }} />
                                        <Typography sx={{ fontSize: '13px', color: dsVar('fg-2') }}>
                                          Last run: {formatDateString(latestRun.date)}
                                        </Typography>
                                      </Box>
                                      <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: dsVar('fg-muted')
                                      }}>
                                        <CategoryIcon sx={{ fontSize: 16, color: dsVar('accent-fg') }} />
                                        <Typography sx={{ fontSize: '13px', color: dsVar('fg-2') }}>
                                          {latestRun.productName}
                                        </Typography>
                                      </Box>
                                    </>
                                  )}
                                </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              <TableContainer>
                                <Table>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        align="left"
                                        sx={{
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          color: dsVar('fg-2'),
                                          padding: '16px',
                                          backgroundColor: dsVar('page'),
                                          borderBottom: `1px solid ${dsVar('line')}`
                                        }}
                                      >
                                        Agent Type
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        sx={{
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          color: dsVar('fg-2'),
                                          padding: '16px',
                                          backgroundColor: dsVar('page'),
                                          borderBottom: `1px solid ${dsVar('line')}`
                                        }}
                                      >
                                        Execution Status
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        sx={{
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          color: dsVar('fg-2'),
                                          padding: '16px',
                                          backgroundColor: dsVar('page'),
                                          borderBottom: `1px solid ${dsVar('line')}`
                                        }}
                                      >
                                        Date & Time
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {agentRuns.map((row, index) => (
                                      <TableRow
                                        key={index}
                                        onClick={() => handleRowClick(row)}
                                        sx={{
                                          cursor: 'pointer',
                                          '&:hover': {
                                            backgroundColor: dsVar('page')
                                          },
                                          transition: 'all 0.2s ease-in-out',
                                          borderBottom: `1px solid ${dsVar('line')}`
                                        }}
                                      >
                                        <TableCell align="left" sx={{ padding: '16px' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: dsVar('fg-soft') }}>
                                            <CategoryIcon sx={{ color: dsVar('accent-fg'), fontSize: 16 }} />
                                            {row.productName}
                                          </Box>
                                        </TableCell>
                                        <StatusCell status={row.status} />
                                        <TableCell align="center" sx={{ padding: '16px' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: dsVar('fg-soft') }}>
                                            <AccessTimeIcon sx={{ color: dsVar('accent-fg'), fontSize: 16 }} />
                                            {formatDateString(row.date)}
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </AccordionDetails>
                          </Accordion>
                        );
                      });
                    })()
                  )}
                </Box>
              </Grid>
            </Grid>

            <Popover
              id={id}
              open={open}
              anchorEl={filterAnchorEl}
              onClose={handleFilterClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: '12px',
                  backgroundColor: dsVar('surface'),
                  border: `1px solid ${dsVar('line')}`,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
                }
              }}
            >
              <Box sx={{ padding: '16px', minWidth: '200px' }}>
                <Typography sx={{
                  color: dsVar('fg-body'),
                  fontSize: '14px',
                  fontWeight: 600,
                  mb: 2
                }}>
                  Filter by Type
                </Typography>
                <FormControl fullWidth>
                  <List sx={{ p: 0 }}>
                    {flowTemplateOptions.map((name) => (
                      <MenuItem
                        key={name}
                        onClick={() => handleFilterChange(name)}
                        sx={{
                          borderRadius: '8px',
                          mb: 0.5,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 222, 89, 0.05)'
                          }
                        }}
                      >
                        <Checkbox
                          checked={selectedFilters.includes(name)}
                          sx={{
                            color: dsVar('fg-2'),
                            '&.Mui-checked': {
                              color: dsVar('accent-fg'),
                            }
                          }}
                        />
                        <ListItemText
                          primary={name}
                          primaryTypographyProps={{
                            fontSize: '14px',
                            color: dsVar('fg-soft')
                          }}
                        />
                      </MenuItem>
                    ))}
                  </List>
                </FormControl>
              </Box>
            </Popover>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AdminPanel;
